import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
  createUDPUiTypeMapper,
} from '@wix/editor-elements-integrations';
import type { Direction } from '@wix/editor-elements-common-utils';
import {
  getMigratedStyleProps,
  convertPhysicalAlignmentToLogical,
  withFallbackValues,
  withSiteDirection,
  castToInheritIfNeeded,
} from '@wix/editor-elements-common-utils';
import type {
  ICollapsibleTextMapperProps,
  CollapsibleTextDefinition,
  ButtonAlignment,
  ICollapsibleTextStateValues,
} from '../CollapsibleText.types';
import { alignmentToStyle } from '../constants';

export const props = withCompInfo<
  ICollapsibleTextMapperProps,
  CollapsibleTextDefinition
>()(
  [
    'compData',
    'compProps',
    'compStylableClass',
    'isConnectedToDataBinding',
    'styleProperties',
    'compId',
    'fullNameCompType',
    'language',
    'mainPageId',
    'trackClicksAnalytics',
  ],
  ({
    compData,
    compProps,
    styleProperties,
    isConnectedToDataBinding,
    compStylableClass: stylableClassName,
    ...rest
  }) => {
    const migratedProps = getMigratedStyleProps({
      compProps,
      styleProperties,
      migratedFields: ['alignButton'],
    });

    return {
      ...compData,
      ...(isConnectedToDataBinding ? { tag: 'div' } : {}),
      ...compProps,
      ...rest,
      ...migratedProps,
      stylableClassName,
      // TODO: Change when correct type is added to TB
      link: (compData.link || null) as ICollapsibleTextMapperProps['link'],
    };
  },
);

export const css = withCompInfo()(
  ['compData', 'compProps', 'styleProperties'],
  ({ compData, compProps, styleProperties }) => {
    const { maxLines, expandControlSpacing } = compProps;
    const {
      direction = 'inherit',
      textDirection = 'inherit',
      buttonDirection = 'inherit',
    } = compData;
    const { alignment, textAlign, buttonAlign } = styleProperties;

    const migratedProps = getMigratedStyleProps({
      compProps,
      styleProperties,
      migratedFields: ['alignButton'],
    });

    const alignSelf =
      alignmentToStyle[migratedProps.alignButton as ButtonAlignment] ||
      alignmentToStyle.left;
    const mainContainerDirection = withSiteDirection(direction);
    return {
      height: 'auto',
      '--max-lines': maxLines,
      '--expand-control-spacing': `${expandControlSpacing}px`,
      '--expand-control-align': `${alignSelf}`, // is not used anymore, except Blocks/Stylable
      '--alignment': `${alignment}`,
      '--paragraph-align': `${textAlign}`,
      '--button-align': `${buttonAlign}`,
      '--direction': mainContainerDirection,
      '--paragraph-direction': `${textDirection}`,
      '--button-direction': `${buttonDirection}`,
    };
  },
  [
    withFallbackValues([
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'compData',
        fields: [{ source: 'text-direction', target: 'textDirection' }],
        enhancer: (direction, mapperArgs) =>
          castToInheritIfNeeded(direction, mapperArgs.compProps.direction),
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'compData',
        fields: [{ source: 'button-direction', target: 'buttonDirection' }],
        enhancer: (direction, mapperArgs) =>
          castToInheritIfNeeded(direction, mapperArgs.compProps.direction),
      },
      {
        sourceNamespace: 'styleProperties',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'text-text-align', target: 'textAlign' }],
        enhancer: (textAlignment, { styleProperties }) => {
          if (textAlignment === 'justify') {
            return 'justify';
          }
          return convertPhysicalAlignmentToLogical(
            textAlignment,
            styleProperties['text-direction'] as Direction,
          );
        },
      },
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'styleProperties',
        fields: [{ source: 'alignButton', target: 'buttonAlign' }],
        enhancer: (textAlignment, { styleProperties }) =>
          convertPhysicalAlignmentToLogical(
            textAlignment,
            styleProperties['button-direction'] as Direction,
          ),
      },
    ]),
  ],
);

export const requiredStateRefs: Array<keyof ICollapsibleTextStateValues> = [
  'reportBi',
];

export const stateRefs =
  withStateRefsValues<keyof ICollapsibleTextStateValues>(requiredStateRefs);

export const uiType = createUDPUiTypeMapper('Default', 'CollapsibleText');

export default createComponentMapperModel({
  props,
  css,
  stateRefs,
  uiType,
});
